'use client';
import Link from 'next/link';
import styled from 'styled-components';

export const StyledFooter = styled.footer`
  background-color: ${props =>
    props.theme.colors.get(
      props.$backgroundColor,
      props.theme.colors.hero.deepPlum,
    )};
  width: 100%;
`;

export const ConstrainedContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing(20, 6, 0)};
  @media ${props => props.theme.tablet} {
    padding: ${props => props.theme.spacing(28, 8, 0)};
  }
`;

export const Paragraph = styled.p`
  font-size: 14px;
  color: #ffffff80;
  line-height: 1.375;
  margin-top: ${props => props.theme.spacing(4)};
`;

export const Content = styled.li`
  list-style: none;
  color: ${props => props.theme.colors.text.lightest};
  font-size: 1.125rem;
  line-height: 1.75rem;
  margin-bottom: ${props => props.theme.spacing(4)};
  @media ${props => props.theme.tablet} {
    font-size: 1.25rem;
  }
  p {
    font-size: 0.875rem;
    line-height: 1.5;
  }
  strong,
  b {
    font-size: 1.125rem;
    @media ${props => props.theme.tablet} {
      font-size: 1.25rem;
    }
  }

  &:empty {
    margin: 0;
  }
`;

export const StyledLink = styled(Link)`
  ${props => props.theme.typography.body}
  font-size: ${props => (props.$hasIcon ? '0.875rem' : '1.125rem')};
  line-height: 1.75rem;
  color: #ffffffcc;
  @media ${props => props.theme.tablet} {
    font-size: ${props => (props.$hasIcon ? '0.875rem' : '1.25rem')};
  }
  &:hover {
    color: ${props => props.theme.colors.hero.lilac};
  }
  &:focus {
    text-decoration: none;
    border: 1px solid ${props => props.theme.colors.background.base};
    border-radius: 2px;
    margin: -1px;
  }
`;

export const LeftAlignedLinks = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  width: 100%;
  grid-column-gap: ${props => props.theme.spacing(6)};
  @media ${props => props.theme.tablet} {
    justify-content: flex-start;
    text-align: left;
    width: auto;
  }
`;

export const FooterDivider = styled.hr`
  border: none;
  border-top: 1px solid #ffffff1a;
  margin-top: ${props => props.theme.spacing(16)};
  margin-bottom: 0;
`;

export const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${props => props.theme.spacing(6)};
  padding-top: ${props => props.theme.spacing(8)};
  ${StyledLink} {
    ${props => props.theme.typography.label}
    color: #ffffff99;
    &:hover {
      color: ${props => props.theme.colors.hero.lilac};
    }
  }
  @media ${props => props.theme.tablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0;
  }
`;

export const CopyrightText = styled.span`
  ${props => props.theme.typography.label}
  color: #ffffff99;
  &:hover {
    color: ${props => props.theme.colors.hero.lilac};
  }
  align-self: center;
  @media ${props => props.theme.tablet} {
    align-self: auto;
  }
`;

export const FooterColumns = styled.div`
  display: flex;
  flex-flow: column;
  margin-bottom: ${props => props.theme.spacing(8)};
  @media ${props => props.theme.laptop} {
    flex-flow: row nowrap;
    justify-content: space-between;
    gap: ${props => props.theme.spacing(6)};
  }
`;

export const ColumnTitle = styled.h5`
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${props => props.theme.colors.text.lightest};
  margin-bottom: ${props =>
    props.$sectionTitle ? props.theme.spacing(4) : '0'};
  display: ${props =>
    props.$alwaysVisible ? 'block' : props.$desktopOnly ? 'none' : 'block'};
  @media ${props => props.theme.laptop} {
    font-size: 1rem;
    line-height: 1.25rem;
    display: ${props =>
      props.$alwaysVisible ? 'block' : props.$desktopOnly ? 'block' : 'none'};
    margin-bottom: ${props =>
      props.$sectionTitle ? props.theme.spacing(3) : props.theme.spacing(4)};
    text-align: left;
  }
`;

export const ToggleCircle = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.neutral.white};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: border-color 0.3s ease;
`;

export const ToggleBar = styled.span`
  position: absolute;
  width: 12px;
  height: 1.5px;
  border-radius: 1px;
  background-color: ${props => props.theme.colors.neutral.white};
  transition: background-color 0.3s ease;
`;

export const ToggleBarVertical = styled(ToggleBar)`
  transition: background-color 0.3s ease, transform 0.3s ease, opacity 0.3s ease;
  transform: rotate(90deg)
    ${props => (props.$isOpen ? 'scaleX(0)' : 'scaleX(1)')};
  opacity: ${props => (props.$isOpen ? 0 : 1)};
`;

export const ColumnToggle = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: ${props => props.theme.spacing(4, 0)};
  cursor: pointer;
  color: ${props => props.theme.colors.text.lightest};
  text-align: left;

  &:hover {
    ${ToggleCircle} {
      border-color: ${props => props.theme.colors.secondary.lilac};
    }
    ${ToggleBar} {
      background-color: ${props => props.theme.colors.secondary.lilac};
    }
  }

  @media ${props => props.theme.laptop} {
    display: none;
  }
`;

export const ColumnDivider = styled.hr`
  border: none;
  border-top: 1px solid #ffffff1a;
  margin: 0;
  @media ${props => props.theme.laptop} {
    display: none;
  }
`;

export const ColumnContent = styled.div`
  @media (max-width: 1023px) {
    overflow: hidden;
    max-height: ${props => (props.$isOpen ? '600px' : '0')};
    opacity: ${props => (props.$isOpen ? '1' : '0')};
    transition: all 0.3s ease-in-out, opacity 0.2s ease-in-out;
    padding-bottom: ${props => (props.$isOpen ? props.theme.spacing(4) : '0')};
  }
`;

export const StyledFooterColumn = styled.ul`
  padding-left: 0;
  text-align: left;
  width: 100%;

  &:last-child {
    margin-top: ${props => props.theme.spacing(8)};
  }

  /* stylelint-disable-next-line no-descending-specificity */
  ${ColumnTitle} {
    &:nth-of-type(2) {
      margin-top: ${props => props.theme.spacing(8)};
    }
  }

  @media ${props => props.theme.laptop} {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
    text-align: left;
    &:last-child {
      margin-top: 0;
    }
  }
`;

export const FooterColumnLink = styled.li`
  display: ${props => (props.icon ? 'grid' : 'flex')};
  grid-template-columns: ${props => (props.icon ? '25px 1fr' : null)};
  grid-column-gap: ${props => (props.icon ? '4px' : null)};
  align-items: end;
  &:not(:last-child) {
    margin-bottom: ${props => props.theme.spacing(3)};
  }
`;

export const BusinessHours = styled.li`
  display: inline-block;
  list-style: none;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #ffffffb3;
  margin-bottom: ${props => props.theme.spacing(4)};
  p {
    font-size: 1.125rem;
    line-height: 1.75rem;
    margin-bottom: ${props => props.theme.spacing(2)};
  }
  strong:first-of-type,
  b:first-of-type {
    display: block;
    color: #ffffffe6;
    margin-bottom: ${props => props.theme.spacing(3)};
  }
  @media ${props => props.theme.tablet} {
    font-size: 1.25rem;
    p {
      font-size: 1.25rem;
    }
  }
  &:empty {
    display: none;
  }
`;

export const LegalBlurb = styled.span`
  display: inline-block;
  list-style: none;
  font-size: 14px;
  line-height: 1.375;
  color: #ffffff80;
  margin-top: ${props => props.theme.spacing(8)};
  &:empty {
    display: none;
  }
  text-align: left;
  p {
    font-size: 14px;
    line-height: 1.375;
  }
`;

export const LinkIconContainer = styled.div`
  margin-right: ${props => props.theme.spacing(1)};
`;

export const SocialIcon = styled.a`
  display: inline-block;
  margin-top: ${props => props.theme.spacing(4)};
  width: 24px;
  &:not(:last-child) {
    margin-right: ${props => props.theme.spacing(5)};
  }
  @media ${props => props.theme.laptop} {
    margin-top: ${props => props.theme.spacing(2)};
    width: 20px;
  }
  &:hover svg {
    fill: ${props => props.theme.colors.hero.lilac};
    path {
      fill: ${props => props.theme.colors.hero.lilac};
    }
  }
  &:focus {
    outline: 1px solid ${props => props.theme.colors.background.base};
  }
`;

export const OllieAppButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(4)};
  margin-top: ${props => props.theme.spacing(4)};
`;

export const SocialIconsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 44px);
  gap: ${props => props.theme.spacing(3)};
  margin-top: ${props => props.theme.spacing(4)};
  @media ${props => props.theme.tablet} {
    grid-template-columns: repeat(3, 44px);
  }

  img {
    width: 44px;
    height: 44px;
    object-fit: contain;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    &:hover img {
      filter: brightness(0) saturate(100%) invert(83%) sepia(28%) saturate(500%)
        hue-rotate(233deg) brightness(103%) contrast(104%);
    }
  }
`;

export const AppStoreIconsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(3)};
  @media ${props => props.theme.tablet} {
    gap: ${props => props.theme.spacing(4)};
  }

  a {
    display: inline-flex;
    transition: transform 0.2s ease;
    &:hover {
      transform: translateY(-2px);
    }
  }

  /* stylelint-disable-next-line no-descending-specificity */
  img {
    height: 62px;
    width: auto;
  }
`;

export const FooterWordmark = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${props => props.theme.spacing(8)};
  pointer-events: none;
  user-select: none;
`;

export const CroppedWordmark = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  /* the wordmark image is 1857x900; locking the aspect ratio to the
     visible top 70% of its height crops the rest without leaving
     empty space below it */
  aspect-ratio: 619 / 175;

  /* stylelint-disable-next-line no-descending-specificity */
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
    opacity: 0.15;
    filter: brightness(0) invert(1);
  }
`;

export const StyledBlurb = styled.p`
  ${props => props.theme.typography.body}
  color: ${props => props.theme.colors.text.lightest};
  text-align: left;
  white-space: nowrap;
  padding-left: ${props => props.theme.spacing(7.5)};
`;
